///
/// EarthBrowser - Compact Time Display
///
/// Created by Matt Giger on 1/21/2025
/// Copyright (c) 2025 Geodata Labs
///

import { setClockCurrentTime, setClockState, setTimeZoneMode, type TimeZoneMode } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { clockDateTimeParts, timeZoneLabel } from "@/utils/timeControl"
import { cn } from "@/utils/utils"
import { time, type time_element } from "@stovokor/math/time"
import { useEffect, useMemo, useRef, useState } from "react"

type ClockParts = Record<time_element, number>
type Draft = Record<time_element, string>

const cElementOrder: time_element[] = ["year", "month", "day", "hour", "minute", "second"]

/** Calendar/clock components of an instant in the display zone */
function partsInZone(ms: number, mode: TimeZoneMode): ClockParts {
	const d = new Date(ms)
	return mode === "utc"
		? { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes(), second: d.getUTCSeconds() }
		: { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() }
}

/** Instant for the given components in the display zone (overflowing fields carry, like Date) */
function msFromParts(p: ClockParts, mode: TimeZoneMode): number {
	return mode === "utc"
		? Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
		: new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second).getTime()
}

function draftFromMs(ms: number, mode: TimeZoneMode): Draft {
	const p = partsInZone(ms, mode)
	const pad = (element: time_element) => p[element].toString().padStart(element === "year" ? 4 : 2, "0")
	return { year: pad("year"), month: pad("month"), day: pad("day"), hour: pad("hour"), minute: pad("minute"), second: pad("second") }
}

/**
 * Parse a draft into an instant. Blank or unparsable fields fall back to
 * the value they had when editing began, so a half-typed field can't fling
 * the globe to year 2.
 */
function msFromDraft(draft: Draft, fallbackMs: number, mode: TimeZoneMode): number {
	const fallback = partsInZone(fallbackMs, mode)
	const parts = { ...fallback }
	for (const element of cElementOrder) {
		const value = parseInt(draft[element], 10)
		if (Number.isFinite(value)) {
			const [min, max] = time.elementRange(element)
			parts[element] = Math.max(min, Math.min(max, value))
		}
	}
	const ms = msFromParts(parts, mode)
	return Number.isFinite(ms) ? ms : fallbackMs
}

function otherZone(mode: TimeZoneMode): TimeZoneMode {
	return mode === "utc" ? "local" : "utc"
}

/**
 * Zone badge that doubles as the local/UTC switch: it shows the zone the
 * clock is displayed in ("PDT", "UTC") and a click flips to the other.
 */
function TimeZoneToggle({ ms, mode, onToggle, className }: {
	ms: number
	mode: TimeZoneMode
	onToggle: () => void
	className?: string
}) {
	const label = timeZoneLabel(ms, mode)
	const otherLabel = mode === "utc" ? timeZoneLabel(ms, "local") : "UTC"
	return (
		<button
			type="button"
			onClick={onToggle}
			className={cn(
				"flex items-center px-1.5 text-[9px] sm:text-[10px] font-sans font-semibold tracking-wide transition-colors",
				"border-l border-white/10 hover:bg-white/10",
				mode === "utc" ? "text-cyan-200" : "text-white/50 hover:text-white/85",
				className
			)}
			title={`Times shown in ${mode === "utc" ? "UTC" : `your local time (${label})`} — click to switch to ${otherLabel}`}
			aria-label={`Switch time display to ${otherLabel}`}
		>
			{label}
		</button>
	)
}

/** Year/month/day field order and separators for the user's locale */
function useDateFieldLayout(): Array<{ element: time_element } | { literal: string }> {
	return useMemo(() => {
		const parts = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(2024, 0, 15))
		const layout: Array<{ element: time_element } | { literal: string }> = []
		for (const part of parts) {
			if (part.type === "year" || part.type === "month" || part.type === "day") {
				layout.push({ element: part.type })
			} else if (part.type === "literal" && layout.length > 0) {
				layout.push({ literal: part.value })
			}
		}
		// drop a trailing literal (e.g. the "." some locales append)
		const last = layout[layout.length - 1]
		if (last && "literal" in last) layout.pop()
		return layout
	}, [])
}

/**
 * Compact clock display that turns into an inline date/time editor on click.
 * Edits are buffered in a draft and committed on Enter or when focus leaves
 * the editor; Escape discards. Shown in the user's locale and the selected
 * display zone (local or UTC), with the zone abbreviation as a suffix.
 *
 * `segment` drops the field's own shell (background, border, height) so it
 * can sit inside a parent chip; `onEditingChange` lets that parent react
 * to the editor opening (e.g. give it a full-width line on phones).
 */
export function TimeValueFieldCompact({ className, segment = false, onEditingChange }: {
	className?: string
	segment?: boolean
	onEditingChange?: (editing: boolean) => void
}) {
	const dispatch = useAppDispatch()
	const clockCurrentTime = useAppSelector(state => state.earthbrowser.clockCurrentTime)
	const timeZoneMode = useAppSelector(state => state.earthbrowser.timeZoneMode)

	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState<Draft | null>(null)
	useEffect(() => {
		onEditingChange?.(isEditing)
	}, [isEditing, onEditingChange])
	// Time when editing began: the fallback for blank/invalid fields
	const editBaseMs = useRef(clockCurrentTime)
	const inputRefs = useRef<Partial<Record<time_element, HTMLInputElement | null>>>({})
	const containerRef = useRef<HTMLDivElement>(null)
	const dateLayout = useDateFieldLayout()

	// Visual order of fields for arrow-key navigation
	const fieldOrder = useMemo<time_element[]>(() => {
		const dateFields = dateLayout.filter((p): p is { element: time_element } => "element" in p).map(p => p.element)
		return [...dateFields, "hour", "minute", "second"]
	}, [dateLayout])

	const beginEditing = () => {
		editBaseMs.current = clockCurrentTime
		setDraft(draftFromMs(clockCurrentTime, timeZoneMode))
		setIsEditing(true)
		dispatch(setClockState("paused"))
	}

	// Focus the first field once the editor renders
	useEffect(() => {
		if (isEditing) {
			const first = inputRefs.current[fieldOrder[0]]
			first?.focus()
			first?.select()
		}
	}, [isEditing, fieldOrder])

	const commit = () => {
		if (draft) {
			dispatch(setClockCurrentTime(msFromDraft(draft, editBaseMs.current, timeZoneMode)))
		}
		setIsEditing(false)
		setDraft(null)
	}

	const cancel = () => {
		setIsEditing(false)
		setDraft(null)
	}

	const jumpToNow = () => {
		dispatch(setClockCurrentTime(Date.now()))
		setIsEditing(false)
		setDraft(null)
	}

	// Arrow up/down: step one unit of the focused field, carrying into
	// neighbours (31 Jan + 1 day -> 1 Feb) and rewriting the whole draft
	const nudge = (element: time_element, delta: number) => {
		if (!draft) return
		const base = partsInZone(msFromDraft(draft, editBaseMs.current, timeZoneMode), timeZoneMode)
		base[element] += delta
		const ms = msFromParts(base, timeZoneMode)
		if (Number.isFinite(ms)) {
			setDraft(draftFromMs(ms, timeZoneMode))
		}
	}

	const focusNeighbour = (element: time_element, direction: 1 | -1) => {
		const index = fieldOrder.indexOf(element)
		const next = fieldOrder[(index + direction + fieldOrder.length) % fieldOrder.length]
		const input = inputRefs.current[next]
		input?.focus()
		input?.select()
	}

	if (!isEditing || !draft) {
		return (
			<div className={cn(
				"flex items-stretch overflow-hidden",
				segment ? "h-full" : "h-9 bg-gray-800/50 rounded-lg border border-gray-700/50",
				className
			)}>
				<button
					type="button"
					className={cn(
						"flex-1 flex items-center justify-center pl-2 pr-1.5 sm:pl-2.5 sm:pr-2",
						"text-white/90 text-xs font-mono tabular-nums whitespace-nowrap cursor-text hover:bg-white/10 transition-colors"
					)}
					onClick={beginEditing}
					title="Click to type a date and time"
					aria-label="Edit date and time"
				>
					{/* Phones drop the year and seconds so the field fits a 320px
					    row; seconds render dimmer everywhere so the ticking
					    digits don't pull the eye. Inline wrapper keeps the
					    date/time space from collapsing as a flex item */}
					<span>
						{clockDateTimeParts(clockCurrentTime, timeZoneMode).map((part, i) => (
							<span key={i} className={cn(part.compactHidden && "hidden sm:inline", part.dim && "text-white/50")}>{part.value}</span>
						))}
					</span>
				</button>
				<TimeZoneToggle ms={clockCurrentTime} mode={timeZoneMode} onToggle={() => dispatch(setTimeZoneMode(otherZone(timeZoneMode)))} />
			</div>
		)
	}

	const renderField = (element: time_element) => {
		const [min, max] = time.elementRange(element)
		const maxLength = max.toString().length
		return (
			<input
				key={element}
				ref={el => { inputRefs.current[element] = el }}
				className={cn(
					element === "year" ? "w-10" : "w-6",
					"font-mono text-center text-xs bg-transparent border-0 text-white/90 focus:text-white focus:outline-none focus:bg-white/10 rounded"
				)}
				value={draft[element]}
				inputMode="numeric"
				aria-label={`${element} input`}
				min={min}
				max={max}
				onFocus={e => e.target.select()}
				onChange={e => {
					const digits = e.target.value.replace(/\D/g, "").slice(0, maxLength)
					setDraft({ ...draft, [element]: digits })
					// auto-advance once the field is full, like a date picker
					if (digits.length === maxLength && element !== "second") {
						focusNeighbour(element, 1)
					}
				}}
				onKeyDown={e => {
					switch (e.key) {
						case "ArrowUp": e.preventDefault(); nudge(element, 1); break
						case "ArrowDown": e.preventDefault(); nudge(element, -1); break
						case "ArrowLeft": e.preventDefault(); focusNeighbour(element, -1); break
						case "ArrowRight": e.preventDefault(); focusNeighbour(element, 1); break
						case "Enter": e.preventDefault(); commit(); break
						case "Escape": e.preventDefault(); cancel(); break
					}
				}}
			/>
		)
	}

	return (
		<div
			ref={containerRef}
			className={cn(
				"flex flex-row items-center px-1.5 gap-0.5",
				segment
					? "h-full"
					// Phones: the editor is wider than the row allows, so it takes
					// its own full-width line below the other controls (a parent
					// chip does this itself in segment mode via onEditingChange)
					: "h-9 bg-gray-800/50 border border-blue-500/50 rounded-lg max-sm:basis-full max-sm:order-last",
				className
			)}
			onBlur={e => {
				// Commit when focus leaves the editor entirely (not when
				// hopping between its own fields/buttons)
				const next = e.relatedTarget as Node | null
				if (!next || !containerRef.current?.contains(next)) {
					commit()
				}
			}}
		>
			{dateLayout.map((part, i) =>
				"element" in part
					? renderField(part.element)
					: <span key={`lit-${i}`} className="text-white/50 text-xs">{part.literal.trim() || " "}</span>
			)}
			<span className="px-0.5" />
			{renderField("hour")}
			<span className="text-white/50 text-xs">:</span>
			{renderField("minute")}
			<span className="text-white/50 text-xs">:</span>
			{renderField("second")}
			<TimeZoneToggle
				ms={editBaseMs.current}
				mode={timeZoneMode}
				className="ml-0.5 rounded"
				onToggle={() => {
					// Re-express the draft in the other zone so the instant
					// being edited doesn't change, only its notation
					const next = otherZone(timeZoneMode)
					const ms = msFromDraft(draft, editBaseMs.current, timeZoneMode)
					dispatch(setTimeZoneMode(next))
					setDraft(draftFromMs(ms, next))
				}}
			/>
			<button
				type="button"
				onClick={jumpToNow}
				className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-green-300 bg-green-500/15 hover:bg-green-500/30 transition-colors"
				title="Set the clock to the current time"
				aria-label="Set time to now"
			>
				Now
			</button>
			<button
				type="button"
				onClick={commit}
				className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-blue-200 bg-blue-500/20 hover:bg-blue-500/35 transition-colors"
				title="Apply (Enter) — Escape cancels"
				aria-label="Apply date and time"
			>
				Set
			</button>
		</div>
	)
}
